import api from './api';

export type PiletaCredProfile = 'admin' | 'user';

export interface PiletaChipsState {
  pileta?: {
    active?: string[];
    labels?: string[];
    text?: string;
  };
  bomba?: {
    active?: string[];
    labels?: string[];
    text?: string;
  };
}

export interface PiletaConfig {
  id: number;
  display_name: string;
  host: string;
  port: number;
  admin_username: string;
  admin_password: string;
  user_username: string;
  user_password: string;
  habitacion_id: number | null;
  artefacto_id: number | null;
  enabled: boolean;
  orden?: number;
  mqtt_enabled?: boolean;
  mqtt_port?: number;
  mqtt_username?: string;
  mqtt_password?: string;
  mqtt_topic_pwa?: string;
  mqtt_host_effective?: string;
  mqtt_password_is_default?: boolean;
  updated_at?: string | null;
  artefacto?: { nombre?: string; icono?: string; color_icono?: string } | null;
}

export interface PiletaMqttTestResult {
  ok: boolean;
  connected?: boolean;
  message_received?: boolean;
  host?: string;
  port?: number;
  topic?: string;
  error?: string;
}

export interface PiletaMqttSuggestResult {
  ok: boolean;
  mqtt_port?: number;
  mqtt_host_effective?: string;
  broker_en_esta_raspberry?: boolean;
  broker_ip_reported?: string;
  mqtt_clients?: {
    broker_on_this_pi?: boolean;
    lan?: { host?: string; port?: number };
    local_backend?: { host?: string; port?: number };
    roles?: { broker?: string; clients?: string[] };
  } | null;
  note?: string;
}

export interface PiletaDevice {
  id: number;
  kind?: 'pileta';
  nombre: string;
  display_name?: string;
  host?: string;
  port?: number;
  habitacion_id: number | null;
  artefacto_id: number | null;
  artefacto?: { nombre?: string; icono?: string; color_icono?: string } | null;
  enabled?: boolean;
  orden?: number;
  online?: boolean;
  /** Reservado: mismo badge Lock que otras DeviceCard cuando exista PIN en pileta. */
  pin_required?: boolean;
  chips_state?: PiletaChipsState | null;
  can_view?: boolean;
}

export interface PiletaStatus {
  ok?: boolean;
  online: boolean;
  chips?: PiletaChipsState | null;
  chips_state?: PiletaChipsState | null;
  raw_summary?: Record<string, unknown> | null;
  updated_at?: string | null;
  error?: string | null;
}

export interface PiletaTestResult {
  ok: boolean;
  profile?: string;
  user_name?: string;
  user_role?: string;
  error?: string;
}

export interface PiletaSessionOpen {
  ticket: string;
  expires_in: number;
}

export interface PiletaSessionConsume {
  token: string;
  user_role?: string | null;
  user_name?: string | null;
}

export async function fetchPiletaConfig(): Promise<PiletaConfig> {
  const res = await api.get('/pileta/config');
  return res.data;
}

export async function putPiletaConfig(body: Partial<{
  display_name: string;
  host: string;
  port: number;
  admin_username: string;
  admin_password: string;
  user_username: string;
  user_password: string;
  habitacion_id: number | null;
  enabled: boolean;
  mqtt_enabled: boolean;
  mqtt_port: number;
  mqtt_username: string;
  mqtt_password: string;
  mqtt_topic_pwa: string;
}>): Promise<PiletaConfig> {
  const res = await api.put('/pileta/config', body);
  return res.data;
}

export async function testPiletaCredentials(profile: PiletaCredProfile): Promise<PiletaTestResult> {
  const res = await api.post('/pileta/test', { profile });
  return res.data;
}

export async function testPiletaMqtt(body?: Partial<{
  host: string;
  mqtt_port: number;
  mqtt_username: string;
  mqtt_password: string;
  mqtt_topic_pwa: string;
}>): Promise<PiletaMqttTestResult> {
  const res = await api.post('/pileta/mqtt/test', body || {});
  return res.data;
}

export async function suggestPiletaMqttFromPi(): Promise<PiletaMqttSuggestResult> {
  const res = await api.post('/pileta/mqtt/suggest-from-pi');
  return res.data;
}

export async function fetchPiletaDevice(): Promise<PiletaDevice> {
  const res = await api.get('/pileta/device');
  return res.data;
}

export async function patchPiletaDevice(body: Record<string, unknown>): Promise<PiletaDevice> {
  const res = await api.patch('/pileta/device', body);
  return res.data;
}

export async function openPiletaSession(): Promise<PiletaSessionOpen> {
  const res = await api.post('/pileta/session/open');
  return res.data;
}

export async function consumePiletaSession(ticket: string): Promise<PiletaSessionConsume> {
  const res = await api.post('/pileta/session/consume', { ticket });
  return res.data;
}

export async function fetchPiletaStatus(): Promise<PiletaStatus> {
  const res = await api.get('/pileta/status');
  return res.data;
}

export type PiletaFuncion =
  | 'filtrar' | 'limpiar' | 'vaciar' | 'calentar' | 'luces'
  | 'canilla' | 'tanque' | 'riego' | 'pileta';

export const PILETA_FUNCS: PiletaFuncion[] = ['filtrar', 'limpiar', 'vaciar', 'calentar', 'luces'];
export const BOMBA_FUNCS: PiletaFuncion[] = ['canilla', 'tanque', 'riego', 'pileta'];

export async function controlPiletaFuncion(
  sistema: 'pileta' | 'bomba',
  funcion: PiletaFuncion,
  accion: 'on' | 'off',
): Promise<void> {
  const path = sistema === 'pileta' ? '/pileta/mcp/pileta/control' : '/pileta/mcp/bomba/control';
  await api.post(path, { funcion, accion });
}

export async function apagarPiletaSistema(sistema: 'pileta' | 'bomba'): Promise<void> {
  await api.post('/pileta/mcp/apagar', { sistema });
}

export interface PiletaPolicyRow {
  pileta_device_id: number;
  whitelist: boolean;
  blacklist: boolean;
  require_confirmation?: boolean;
  defined?: boolean;
}

export async function listPiletaPolicies(): Promise<PiletaPolicyRow[]> {
  try {
    const res = await api.get<{ policies: PiletaPolicyRow[] }>('/pileta/ai-policies');
    return res.data.policies || [];
  } catch {
    return [];
  }
}

export async function getPiletaPolicy(deviceId: number): Promise<PiletaPolicyRow | null> {
  try {
    const res = await api.get<PiletaPolicyRow>(`/pileta/devices/${deviceId}/ai-policy`);
    if (!res.data || res.data.defined === false) return null;
    return res.data;
  } catch {
    return null;
  }
}

export async function upsertPiletaPolicy(
  deviceId: number,
  body: { whitelist?: boolean; blacklist?: boolean; require_confirmation?: boolean },
): Promise<PiletaPolicyRow> {
  const res = await api.put(`/pileta/devices/${deviceId}/ai-policy`, body);
  return res.data;
}

export async function deletePiletaPolicy(deviceId: number): Promise<void> {
  await api.delete(`/pileta/devices/${deviceId}/ai-policy`);
}

export async function deletePiletaDevice(deviceId: number): Promise<void> {
  await api.delete(`/pileta/devices/${deviceId}`);
}
